import { noteDisplay } from './tableTicket'

export type CupLabelMedia = 'sticky' | 'receipt' | 'liner'

export type CupLabelFontScale = 'compact' | 'regular' | 'large'

export const CUP_LABEL_PIECES = [
  'itemName',
  'option',
  'advanceOption',
  'advanceQty',
  'quantity',
  'instructions',
  'orderNumber',
  'customerName',
  'time',
  'source',
  'storeName',
  'sequence',
] as const

export type CupLabelPiece = typeof CUP_LABEL_PIECES[number]

export type CupLabelTemplate = {
  id: string
  name: string
  enabled: boolean
  media: CupLabelMedia
  widthMm: number
  heightMm: number
  rows: CupLabelPiece[][]
  fontScale: CupLabelFontScale
  rotate90: boolean
}

export type CupLabelLine = {
  text: string
  bold: boolean
  role: 'title' | 'body'
}

export const CUP_LABEL_PRESETS = [
  { id: '40x30', widthMm: 40, heightMm: 30 },
  { id: '40x50', widthMm: 40, heightMm: 50 },
  { id: '50x30', widthMm: 50, heightMm: 30 },
  { id: '58x40', widthMm: 58, heightMm: 40 },
  { id: '58', widthMm: 58, heightMm: 50 },
  { id: '80', widthMm: 80, heightMm: 50 },
] as const

const PIECE_ALIAS: Record<string, CupLabelPiece> = {
  itemName: 'itemName',
  drinkName: 'itemName',
  option: 'option',
  size: 'option',
  advanceOption: 'advanceOption',
  modifiers: 'advanceOption',
  advanceQty: 'advanceQty',
  quantity: 'quantity',
  instructions: 'instructions',
  orderNumber: 'orderNumber',
  customerName: 'customerName',
  time: 'time',
  source: 'source',
  storeName: 'storeName',
  sequence: 'sequence',
}

const DEFAULT_ROWS: CupLabelPiece[][] = [
  ['orderNumber'],
  ['itemName'],
  ['advanceQty', 'advanceOption'],
  ['instructions'],
]

export function defaultCupLabelTemplate(id = ''): CupLabelTemplate {
  return {
    id,
    name: '',
    enabled: true,
    media: 'sticky',
    widthMm: 40,
    heightMm: 50,
    rows: DEFAULT_ROWS.map((row) => [...row]),
    fontScale: 'regular',
    rotate90: false,
  }
}

function pieceName(value: unknown): CupLabelPiece | '' {
  return PIECE_ALIAS[String(value || '')] || ''
}

function rowsFrom(raw: any): CupLabelPiece[][] {
  if (Array.isArray(raw?.rows)) {
    const rows = raw.rows.map((row: unknown) => {
      const source = Array.isArray(row)
        ? row
        : (row && typeof row === 'object' && Array.isArray((row as { pieces?: unknown }).pieces)
          ? (row as { pieces: unknown[] }).pieces
          : [])
      return source.map(pieceName).filter((piece): piece is CupLabelPiece => Boolean(piece))
    }).filter((row: CupLabelPiece[]) => row.length > 0)
    if (rows.length) return rows
  }
  const requested = Array.isArray(raw?.lineOrder) ? raw.lineOrder : Object.keys(raw?.fields || {})
  const rows: CupLabelPiece[][] = []
  requested.forEach((key: unknown) => {
    const piece = pieceName(key)
    if (!piece) return
    if (raw?.fields && raw.fields[String(key)] === false) return
    if (piece === 'advanceOption') rows.push(['advanceQty', 'advanceOption'])
    else rows.push([piece])
  })
  return rows.length ? rows : DEFAULT_ROWS.map((row) => [...row])
}

function clamp(value: unknown, min: number, max: number, fallback: number) {
  const number = Number(value)
  if (!Number.isFinite(number)) return fallback
  return Math.min(max, Math.max(min, Math.round(number)))
}

export function normalizeCupLabelTemplate(raw: any): CupLabelTemplate {
  const base = defaultCupLabelTemplate(String(raw?.id || ''))
  const media = raw?.media === 'receipt' || raw?.media === 'liner' || raw?.media === 'sticky'
    ? raw.media
    : base.media
  const fontScale = raw?.fontScale === 'compact' || raw?.fontScale === 'large' || raw?.fontScale === 'regular'
    ? raw.fontScale
    : base.fontScale
  return {
    id: base.id,
    name: String(raw?.name || '').trim().slice(0, 40),
    enabled: raw?.enabled !== false,
    media,
    widthMm: clamp(raw?.widthMm, 20, 120, 40),
    heightMm: clamp(raw?.heightMm, 20, 300, 50),
    rows: rowsFrom(raw),
    fontScale,
    rotate90: raw?.rotate90 === true,
  }
}

export function paperForCupLabel(template?: { media?: string } | null): 'receipt' | 'linerless' {
  return template?.media === 'receipt' ? 'receipt' : 'linerless'
}

export function syncPrinterTemplates(printerSettings: any[], templates: CupLabelTemplate[]) {
  const byId = new Map(templates.map((template) => [template.id, template]))
  return (printerSettings || []).map((printer) => {
    const id = String(printer?.itemsTab?.cupLabelTemplateId || '')
    if (!id) return printer
    const next = byId.get(id)
    if (!next || next.enabled === false) {
      return {
        ...printer,
        itemsTab: {
          ...printer.itemsTab,
          cupLabelTemplateId: '',
          cupLabelTemplate: null,
        },
      }
    }
    return {
      ...printer,
      itemsTab: {
        ...printer.itemsTab,
        cupLabelTemplateId: next.id,
        cupLabelTemplate: next,
        cupLabelPaper: paperForCupLabel(next),
      },
    }
  })
}

export function findCupLabelTemplate(store: any, printer: any): CupLabelTemplate | null {
  const key = String(printer?.api_key || '').trim()
  if (!key) return null
  const settings = Array.isArray(store?.printerSettings) ? store.printerSettings : []
  const entry = settings.find((item: any) => {
    const api = String(item?.generalTab?.apikey || item?.generalTab?.apiKey || '').trim()
    return api === key
  })
  const raw = entry?.itemsTab?.cupLabelTemplate
  if (!raw || raw.enabled === false) return null
  return normalizeCupLabelTemplate(raw)
}

function optionName(item: any) {
  const selected = item?.selectedOption
  if (!selected) return ''
  if (typeof selected === 'string') return selected.trim()
  return String(selected.printName || selected.name || '').trim()
}

function advanceSelections(item: any) {
  const groups = Array.isArray(item?.advancedOptions?.selections) ? item.advancedOptions.selections : []
  const chosen: { name: string; qty: number }[] = []
  groups.forEach((group: any) => {
    const options = Array.isArray(group?.optionSet?.options) ? group.optionSet.options : []
    const printByName = new Map<string, string>(
      options.map((option: any) => [String(option?.name || ''), String(option?.printName || '').trim()])
    )
    const names = options.map((option: any) => String(option?.name || '')).filter(Boolean)
    Object.keys(group?.selections || {}).forEach((name) => {
      if (!names.includes(name)) names.push(name)
    })
    names.forEach((name: string) => {
      const picked = group?.selections?.[name]
      const qty = Number(picked?.userQuantity || 0)
      if (!picked || qty <= 0) return
      chosen.push({ name: printByName.get(name) || name, qty })
    })
  })
  return chosen
}

function pieceText(piece: CupLabelPiece, input: {
  itemName: string
  option: string
  amount: number
  order: any
  store: any
  index: number
  total: number
  timeText?: string
  noteLines: string[]
}, selection?: { name: string; qty: number }) {
  if (piece === 'itemName') return input.itemName
  if (piece === 'option') return input.option
  if (piece === 'quantity') return input.amount > 0 ? String(input.amount) : ''
  if (piece === 'advanceOption') return selection?.name || ''
  if (piece === 'advanceQty') return selection && selection.qty > 1 ? String(selection.qty) : ''
  if (piece === 'instructions') return input.noteLines.join(' ')
  if (piece === 'orderNumber') return String(input.order.orderNumber || '').trim()
  if (piece === 'customerName') return String(input.order.guestName || '').trim()
  if (piece === 'time') return String(input.timeText || '').trim()
  if (piece === 'source') return String(input.order.orderSource || input.order.orderChannel || input.order.channel || '').trim()
  if (piece === 'storeName') return String(input.store?.name || '').trim()
  if (piece === 'sequence' && input.total > 0) return `${input.index + 1}/${input.total}`
  return ''
}

export function buildCupLabelLines(input: {
  template: CupLabelTemplate
  item: any
  order: any
  store: any
  index: number
  total: number
  timeText?: string
}): CupLabelLine[] {
  const template = normalizeCupLabelTemplate(input.template)
  const item = input.item || {}
  const order = input.order || {}
  const context = {
    itemName: String(item.printName || item.name || '').trim(),
    option: optionName(item),
    amount: Math.max(0, Math.round(Number(item.amount ?? item.qty ?? 0))),
    order,
    store: input.store,
    index: input.index,
    total: input.total,
    timeText: input.timeText,
    noteLines: noteDisplay(item.itemNote).split('\n').map((line) => line.trim()).filter(Boolean),
  }
  const selections = advanceSelections(item)
  const lines: CupLabelLine[] = []
  template.rows.forEach((row) => {
    if (row.length === 1 && row[0] === 'instructions') {
      context.noteLines.forEach((text) => lines.push({ text, bold: false, role: 'body' }))
      return
    }
    const repeats = row.some((piece) => piece === 'advanceOption' || piece === 'advanceQty')
    const copies = repeats ? (selections.length ? selections : []) : [undefined]
    copies.forEach((selection) => {
      const text = row.map((piece) => pieceText(piece, context, selection)).filter(Boolean).join(' ')
      if (!text) return
      const title = row.length === 1 && row[0] === 'itemName'
      lines.push({ text, bold: title, role: title ? 'title' : 'body' })
    })
  })
  return lines
}

export function sampleCupLabelLines(template: CupLabelTemplate, storeName = ''): CupLabelLine[] {
  return buildCupLabelLines({
    template,
    index: 0,
    total: 1,
    timeText: '10:15 AM',
    store: { name: storeName || 'Store' },
    order: {
      orderNumber: 'DL002',
      guestName: 'Alex',
      orderSource: 'Counter',
    },
    item: {
      name: 'Legendary Spicy Broth (Medium Spicy) / 秦氏牛油火锅（微辣）',
      printName: '',
      amount: 1,
      itemNote: '',
      advancedOptions: {
        selections: [
          {
            optionSet: {
              name: 'Size',
              options: [
                { name: 'small' },
                { name: 'medium' },
                { name: 'large' },
                { name: 'extra large' },
              ],
            },
            selections: { large: { userQuantity: 1 } },
          },
          {
            optionSet: {
              name: 'Extra',
              displayName: 'Extra dispaly',
              options: [
                { name: 'meat' },
                { name: 'lamb' },
                { name: 'pork' },
                { name: 'chikent' },
              ],
            },
            selections: { meat: { userQuantity: 2 } },
          },
        ],
      },
    },
  })
}
